<template>
  <vs-row id="change-logs-list">
    <vs-col>
      <vue-element-loading v-if="loading" :active="loading"/>
      <vs-row>
        <vs-col>
          <card-page card-title="Change Logs">
            <!-- Pending Changes Section -->
            <div v-if="pendingChanges.length > 0" class="p-4">
              <h5 class="mb-4 text-warning">Pending Changes</h5>
              <div class="pending-changes-card">
                <vs-alert color="warning" class="mb-4">
                  <span>There {{ pendingChanges.length === 1 ? 'is' : 'are' }} {{ pendingChanges.length }} pending change{{ pendingChanges.length === 1 ? '' : 's' }} that require{{ pendingChanges.length === 1 ? 's' : '' }} approval or rejection.</span>
                </vs-alert>
                <vs-button
                  color="warning"
                  type="filled"
                  @click="viewPendingChange"
                  class="mb-4"
                >
                  <i class="feather icon-eye mr-2"></i>
                  View Pending Change Request
                </vs-button>
              </div>
            </div>

            <!-- Historical Changes Grid -->
            <div>
              <grid-panel
                id="change-logs"
                height="md-plus"
                :auto-size="true"
                :column-defs="columnDefs"
                :url="() => this.$api.facilities.getChangeLogs(facilityId)"
                class="vx-col w-full p-0 m-0"
                @rowClicked="viewChangeLog"
                :show-search="true"
                title="Historical Changes"
              />
            </div>
          </card-page>
        </vs-col>
      </vs-row>
    </vs-col>
  </vs-row>
</template>

<script>
import GridPanel from '@/views/components/ag-grid/GridPanel';
import CanAccessMixin from "@/components/mixins/CanAccessMixin";
import CardPage from '../../../components/Views/CardPage';
import moment from 'moment';

export default {
  name: 'ChangeLogsList',
  components: {
    GridPanel,
    CardPage,
  },
  mixins: [CanAccessMixin],
  data() {
    return {
      pendingChanges: [],
      loading: false,
      columnDefs: [
        {
          headerName: 'ID',
          field: 'id',
          minWidth: 100,
          maxWidth: 100,
          filter: 'agNumberColumnFilter',
        },
        {
          headerName: 'Requested By',
          field: 'UserFirstName',
          width: 200,
          filter: 'agTextColumnFilter',
          valueFormatter: ({data}) => {
            return `${data.UserFirstName || ''} ${data.UserLastName || ''}`.trim();
          },
        },
        {
          headerName: 'Requested Date',
          field: 'Created_Date',
          minWidth: 150,
          maxWidth: 150,
          filter: 'agDateColumnFilter',
          valueFormatter: ({value}) => {
            return value ? moment(value).format('DD/MM/YYYY') : '';
          },
        },
        {
          headerName: 'Status',
          field: 'status',
          minWidth: 100,
          maxWidth: 100,
          filter: 'agTextColumnFilter',
          cellRenderer: (params) => {
            const status = this.getStatus(params.data);
            const capitalizedStatus = status.charAt(0).toUpperCase() + status.slice(1);
            const color = this.getStatusColor(status);
            return `<span class="vs-chip vs-chip--${color} vs-chip--small">${capitalizedStatus}</span>`;
          },
        },
      ]
    };
  },
  computed: {
    facilityId() {
      return this.$route.params.id;
    }
  },
  async mounted() {
    await this.loadPendingChanges();
  },
  methods: {
    async loadPendingChanges() {
      this.loading = true;
      try {
        const response = await this.$api.facilities.getChangeLogs(this.facilityId);
        const allChangeLogs = (response.data && response.data.data) || response.data || [];
        
        // Get pending changes (not approved and not rejected)
        this.pendingChanges = allChangeLogs.filter(changeLog => 
          !changeLog.Approved && !changeLog.Rejected
        );
      } catch (error) {
        console.error('Error loading pending changes:', error);
        this.$vs.notify({
          color: 'danger',
          title: 'Error',
          text: 'Failed to load pending changes',
          position: 'top-right'
        });
      } finally {
        this.loading = false;
      }
    },
    viewChangeLog({data}) {
      this.$router.push({
        name: 'facilities.change-logs.view',
        params: { id: this.facilityId, changeLogId: data.id }
      });
    },
    viewPendingChange() {
      // Get the latest pending change (should be the first one since we order by Created_Date desc)
      const latestPendingChange = this.pendingChanges[0];
      if (latestPendingChange) {
        this.viewChangeLog({data: latestPendingChange});
      }
    },
    getStatus(changeLog) {
      if (changeLog.Approved) {
        return 'approved';
      }
      if (changeLog.Rejected) {
        return 'rejected';
      }
      return 'pending';
    },
    getStatusColor(status) {
      switch (status) {
        case 'pending':
          return 'warning';
        case 'approved':
          return 'success';
        case 'rejected':
          return 'danger';
        default:
          return 'primary';
      }
    }
  }
};
</script>

<style>
/* Custom CSS for vs-chip classes in AG-Grid */
.vs-chip {
  padding: 4px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  text-transform: capitalize;
}

.vs-chip--small {
  padding: 2px;
  font-size: 11px;
}

.vs-chip--warning {
  background-color: #ffc107;
  color: #212529;
  border: 1px solid #ffc107;
}

.vs-chip--success {
  background-color: #28a745;
  color: white;
  border: 1px solid #28a745;
}

.vs-chip--danger {
  background-color: #dc3545;
  color: white;
  border: 1px solid #dc3545;
}

.vs-chip--primary {
  background-color: #007bff;
  color: white;
  border: 1px solid #007bff;
}
</style>